import { streamDashboardPath, type Stream } from 'Lessons';
import { type Playlist } from 'Playlists';
import { NextUpMobileOnly } from 'NextUp';
import { memo, useEffect, type FC } from 'react';
import { Text } from 'Text';
import { useTranslation } from 'react-i18next';
import { type ProgressStatus } from 'ContentProgress';
import { useAngularContext } from 'AngularContext';
import { type RouteAnimationHelper as RouteAnimationHelperClass } from 'RouteAnimationHelper';
import { CourseList } from 'BookmarkedAndCompletedCourses/CourseList';
import './locales/currentlyStudying/currentlyStudying-en.json';
import { useAppHeaderViewModel } from 'NavigationHelpers';
import { useTargetBrandConfig } from 'FrontRoyalAngular';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { useMatch } from 'react-router-dom';
import { useGetActivePlaylistStreams } from './hooks/useGetActivePlaylistStreams';

type CurrentlyStudyingPresentationProps = {
    streams: Stream[];
    playlist: Playlist | null;
    onPress: (course: Stream) => void;
    getProgressInfo: (stream: Stream) => { status: ProgressStatus } | null;
    numCourses: number;
    numExams: number;
    numComplete: number;
    numProjects: number;
    NextUpMobileBox: React.ReactNode;
};

export const CurrentlyStudyingPresentation: FC<CurrentlyStudyingPresentationProps> = ({
    streams,
    playlist,
    getProgressInfo,
    onPress,
    numCourses,
    numExams,
    numComplete,
    // numProjects,
    NextUpMobileBox,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'currentlyStudying.currentlyStudying' });

    if (streams.length === 0 || !playlist) return null;

    return (
        <div className="flex flex-col p-3.5 pt-[30px] md:p-[30px]">
            <div className="mb-[30px] md:hidden">{NextUpMobileBox}</div>

            <Text
                variant="h1"
                weight="semibold"
                uppercase
                color="black"
                className="mb-1 hidden text-center md:block md:text-start"
            >
                {playlist.title}
            </Text>
            <Text
                variant="h2"
                weight="semibold"
                uppercase
                color="black"
                className="mb-1 block text-center md:hidden md:text-start"
            >
                {playlist.title}
            </Text>

            <Text uppercase className="mb-[30px] text-center md:mb-[15px] md:text-start">
                {t('playlist_status_courses', { num: numCourses })}
                {t('playlist_status_exams', { num: numExams })}
                {/* Temporarily disabled until we have project logic connected */}
                {/* {t('playlist_status_projects', { num: numProjects })} */}
                {t('playlist_status_complete', { num: numComplete })}
            </Text>

            <Text className="mb-[30px] hidden md:block">{playlist.description}</Text>

            <CourseList fullWidth courses={streams} onPress={onPress} getProgressInfo={getProgressInfo} />
        </div>
    );
};

export const CurrentlyStudying: FC = memo(() => {
    const RouteAnimationHelper = useAngularContext().get<RouteAnimationHelperClass>('RouteAnimationHelper');
    const { activePlaylistStreams, activePlaylist, getProgressInfo, numCourses, numExams, numComplete, numProjects } =
        useGetActivePlaylistStreams();
    const onPress = (stream: Stream) => {
        RouteAnimationHelper.animatePathChange(
            streamDashboardPath({ streamTitle: stream.title, streamId: stream.id }),
            'slide-left',
        );
    };
    const AppHeaderViewModel = useAppHeaderViewModel();
    const brandConfig = useTargetBrandConfig();
    const { windowInnerWidth } = useIsMobileMixin();
    const { t } = useTranslation('back_royal');
    const isCurrentlyStudyingRoute = useMatch('/dashboard/currently-studying');

    useEffect(() => {
        if (!isCurrentlyStudyingRoute) return;

        const requiresShortTitle = windowInnerWidth <= 400;
        const institutionName = requiresShortTitle
            ? brandConfig.brandNameShort
            : brandConfig[brandConfig.appHeaderHomeTitleNameProperty];

        AppHeaderViewModel.learningBoxMode = { mode: 'active_playlist' };
        AppHeaderViewModel.setTitleHTML(
            t('lessons.stream.student_dashboard_learning_box.homeTitle', { institutionName }).toLocaleUpperCase(),
            {
                small: !requiresShortTitle,
            },
        );
    }, [AppHeaderViewModel, brandConfig, isCurrentlyStudyingRoute, windowInnerWidth, t]);

    return (
        <CurrentlyStudyingPresentation
            streams={activePlaylistStreams}
            playlist={activePlaylist}
            getProgressInfo={getProgressInfo}
            onPress={onPress}
            numCourses={numCourses}
            numExams={numExams}
            numComplete={numComplete}
            numProjects={numProjects}
            NextUpMobileBox={<NextUpMobileOnly />}
        />
    );
});
